import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Drawer,
  DrawerContent,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  DrawerButtonGroup,
  Button,
  Center,
  CheckboxTreeGroup,
  Checkbox,
  FormLabel,
  FormControl,
  Spinner,
  Text,
  Tooltip,
  VStack,
  OutlineCircleQuestionIcon,
  DrawerCloseButton,
} from '@frontend/components';

import UserGroupSelector from 'modules/common/smartComponents/userGroupSelector/UserGroupSelector';

import { getBusinessName } from 'store/session/selectors';

import { notifyError, notifySuccess } from 'utils/notifyUtils';

import { featureFlagKeys } from '../featureFlagDrawer/const/featureFlagKeys';
import { copyAnalyticsDrawer } from './data/actions';
import { useDashboardsQuery, useCopyDashboardsMutation } from './data/hooks';
import { getIsCopyAnalyticsDrawerOpen } from './data/reducer';

const CopyAnalyticsDrawer = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsCopyAnalyticsDrawerOpen);
  const userGroupBusinessName = useSelector(getBusinessName);

  const dashboardsQueryOptions = {
    refetchOnWindowFocus: false,
    enabled: isOpen,
  };
  const { data: dashboards } = useDashboardsQuery(dashboardsQueryOptions);

  const [selectedUserGroup, setSelectedUserGroup] = useState();
  const [selectedAnalyticsDashboards, setSelectedAnalyticsDashboards] =
    useState([]);
  const [confirmed, setConfirmed] = useState(false);

  const onClose = () => {
    dispatch(copyAnalyticsDrawer.close());
    setConfirmed(false);
    setSelectedAnalyticsDashboards([]);
    setSelectedUserGroup(undefined);
  };

  const getSelectedDashboardsIdList = () => {
    return Object.keys(selectedAnalyticsDashboards).filter(
      (k) => selectedAnalyticsDashboards[k],
    );
  };

  const copyDashboard = useCopyDashboardsMutation({
    onError: (err) => {
      notifyError('Error Copying Dashboard', err.message);
    },
    onSuccess: () => {
      notifySuccess('Dashboard Copied', 'Dashboard copied successfully');
    },
  });

  const onClickCopyDashboard = () => {
    copyDashboard.mutate({
      data: {
        dashboards: getSelectedDashboardsIdList(),
        target_usergroup: selectedUserGroup.value,
      },
    });
    onClose();
  };

  const disableUserGroupOptions = (group) => {
    return !(
      group.featureFlags[featureFlagKeys.isAdvancedAnalyticsEnabled] &&
      group.featureFlags[featureFlagKeys.isQuotingToolAvailable]
    );
  };

  const helpTextComponent = () => (
    <Tooltip
      hasArrow={false}
      label="User group can only be selected if they have both quoting and advanced analytics enabled."
      placement="right"
    >
      <OutlineCircleQuestionIcon size={2} marginLeft={0.5} />
    </Tooltip>
  );

  return (
    <Drawer onClose={onClose} isOpen={isOpen}>
      <DrawerContent>
        <DrawerHeader>
          Copy Analytics Dashboard From&nbsp;{userGroupBusinessName}
          <DrawerCloseButton />
        </DrawerHeader>
        <DrawerBody as={VStack} spacing={1.5}>
          {dashboards ? (
            <>
              <UserGroupSelector
                disabled={false}
                selectedUserGroup={selectedUserGroup}
                onChange={setSelectedUserGroup}
                optionsDisabledFn={disableUserGroupOptions}
                iconToPutInFormLabelFn={helpTextComponent}
              />
              <FormControl>
                <FormLabel>Dashboards</FormLabel>
                <CheckboxTreeGroup
                  parent="(Select All)"
                  value={selectedAnalyticsDashboards}
                  onChange={setSelectedAnalyticsDashboards}
                >
                  {dashboards.map((dashboard) => (
                    <Checkbox name={dashboard.id} key={dashboard.id}>
                      {dashboard.name}
                    </Checkbox>
                  ))}
                </CheckboxTreeGroup>
              </FormControl>
            </>
          ) : (
            <Center>
              <Spinner size="lg" />
            </Center>
          )}
        </DrawerBody>
        <DrawerFooter flexDirection="column" alignItems="flex-end">
          {selectedUserGroup && (
            <Checkbox
              isChecked={confirmed}
              onChange={() => setConfirmed(!confirmed)}
            >
              I understand that I am copying the selected dashboards to&nbsp;
              <Text as="strong" fontWeight="heavy">
                {selectedUserGroup.label}
              </Text>
              .
            </Checkbox>
          )}
          <DrawerButtonGroup>
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={onClickCopyDashboard}
              isDisabled={!confirmed || !getSelectedDashboardsIdList().length}
            >
              Copy Analytics
            </Button>
          </DrawerButtonGroup>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default CopyAnalyticsDrawer;
